import {Link} from 'react-router-dom'
import {Space, Divider} from 'antd'
import styles from './AMLCheckDetails.module.scss'
import useStore from 'base/store'
import {
  Text,
  Button,
  IconButton,
  File,
  Box,
  Tag,
  Modal,
  // Confirm,
  UserAvatar,
  CertificateChekIcon,
  CloseFillIcon,
  PenCircleIcon,
} from 'common/components'
import {useOpen} from 'common/hooks'
import {formatDate} from 'common/helpers'
import {
  AMLApproveForm,
  AMLBeneficiaryForm,
  useResendAMLLink,
  // useUpdateAMLBeneficiary,
} from '..'

interface AMLCheckDetailsProps {
  request: any
  aml: any
  onClose: () => void
}

interface ResendLinkProps {
  amlUuid: string
}

// interface ChangeTypeProps {
//   aml: any
//   projectUuid: string
// }

interface VoteProps {
  status: string
  vote: any
}

interface EditBeneficiaryProps {
  request: any
  aml: any
}

export default function AMLCheckDetails({
  request,
  aml,
  onClose,
}: AMLCheckDetailsProps) {
  const role = useStore((state) => state.role)

  const isGreenID = aml.type === 'green-id'
  const isAMLPending = isGreenID
    ? aml.greenIdStatus !== 'approved'
    : aml.status === 'pending'

  return (
    <>
      <Box className={styles.header} mb={24}>
        {isAMLPending && <EditBeneficiary request={request} aml={aml} />}
        <Text size="3xl" weight="medium" mb={6}>
          {aml.name}
        </Text>
        <Text size="xs" mb={6}>
          Invited {formatDate(aml.createdAt, 'fifth')} via {aml.phone},
          <br />
          {aml.email}
        </Text>
        {!aml.locked && <ResendLink amlUuid={aml.uuid} />}
      </Box>
      {isGreenID ? (
        <Box mb={16}>
          <Box weight="demiBold" mb={4}>
            <Space size={4}>
              Green ID status
              {aml.greenIdStatus === 'approved' ? (
                <>
                  - approved <CertificateChekIcon />
                </>
              ) : aml.greenIdStatus === 'rejected' ? (
                <>
                  - rejected <CloseFillIcon />
                </>
              ) : (
                ''
              )}
            </Space>
          </Box>
          {aml.greenIdStatus !== 'pending' ? (
            <Text mt={6} size="xs">
              {aml.greenIdStatus === 'approved' ? 'Verifyed' : 'Rejected'} on{' '}
              {aml.greenIdStatusUpdatedAt
                ? formatDate(aml.greenIdStatusUpdatedAt)
                : null}
            </Text>
          ) : (
            <Tag>Pending...</Tag>
          )}
        </Box>
      ) : (
        <>
          <Box mb={16}>
            <Text weight="demiBold" mb={4}>
              Identity verification
            </Text>
            {aml.locked ? (
              <Box ml={-10} mr={-10}>
                {aml.identityDocuments.map((document: any) => (
                  <File
                    key={aml.uuid}
                    secondary
                    name={document.name}
                    size={document.size}
                    url={document.url}
                    createdAt={document.createdAt}
                  />
                ))}
              </Box>
            ) : (
              <Tag>Pending...</Tag>
            )}
          </Box>
          <Box mb={16}>
            <Text weight="demiBold" mb={4}>
              Address verification
            </Text>
            {aml.locked ? (
              <Box ml={-10} mr={-10}>
                {aml.addressDocuments.map((document: any) => (
                  <File
                    key={document.uuid}
                    secondary
                    name={document.name}
                    size={document.size}
                    url={document.url}
                    createdAt={document.createdAt}
                  />
                ))}
              </Box>
            ) : (
              <Tag>Pending...</Tag>
            )}
          </Box>
        </>
      )}
      <Box mb={24}>
        <Text weight="demiBold" mb={4}>
          Documents
        </Text>
        <Text size="xs" mb={6}>
          Open the AML folder with all the documents related to the AML check
          and review extra files in it.
        </Text>
        <Link tabIndex={-1} to={`documents/#${aml.uuid}`}>
          <Button variant="secondary">check documents</Button>
        </Link>
        {/* {isAMLPending && (
          <ChangeType aml={aml} projectUuid={request.project.uuid} />
        )} */}
      </Box>
      {aml.status === 'approved' || aml.status === 'rejected' ? (
        <Vote status={aml.status} vote={aml.votes[0]} />
      ) : role.manager ||
        (isGreenID ? aml.greenIdStatus === 'pending' : !aml.locked) ? (
        <Box mt={32}>
          <Button block onClick={onClose}>
            Close
          </Button>
        </Box>
      ) : (
        <>
          <Divider />
          <AMLApproveForm aml={aml} request={request} onClose={onClose} />
        </>
      )}
    </>
  )
}

const ResendLink = ({amlUuid}: ResendLinkProps) => {
  const {mutate: resendAMLLink, isMutating: isResendAMLLinkMutating} =
    useResendAMLLink(amlUuid)

  return (
    <Button
      variant="secondary"
      loading={isResendAMLLinkMutating}
      onClick={resendAMLLink}
    >
      resend link
    </Button>
  )
}

// const ChangeType = ({aml, projectUuid}: ChangeTypeProps) => {
//   const {
//     mutate: updateAMLBeneficiary,
//     isMutating: isUpdateAMLBeneficiaryMutating,
//   } = useUpdateAMLBeneficiary(aml?.uuid, projectUuid)

//   const isGreenID = aml.type === 'green-id'

//   const handleClick = () => {
//     updateAMLBeneficiary({
//       type: isGreenID ? 'manual' : 'green-id',
//     })
//   }

//   const label = `change to ${isGreenID ? 'manual' : 'Green ID'} check`

//   return (
//     <Box mt={24}>
//       <Text size="xs" mb={6}>
//         This is possible to change the type of check at any time:
//       </Text>
//       <Confirm title={`Are you sure you want ${label}`} onConfirm={handleClick}>
//         <Button variant="secondary" loading={isUpdateAMLBeneficiaryMutating}>
//           {label}
//         </Button>
//       </Confirm>
//     </Box>
//   )
// }

const Vote = ({status, vote}: VoteProps) => {
  const {user} = vote

  return (
    <div className={styles.vote}>
      <Space size={12}>
        <UserAvatar size={48} src={user.photo} alt={user.firstName} />
        <Text weight="demiBold">
          {user.firstName} {user.lastName}
          <br />
          on {formatDate(vote.createdAt, 'fifth')}
        </Text>
      </Space>
      {status === 'approved' ? (
        <Tag icon="check" color="green">
          Approved
        </Tag>
      ) : (
        <Tag icon="close" color="red">
          Rejected
        </Tag>
      )}
    </div>
  )
}

const EditBeneficiary = ({request, aml}: EditBeneficiaryProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <IconButton
        className={styles.edit}
        icon={<PenCircleIcon />}
        onClick={handleOpen}
      />
      <Modal
        width={486}
        title="AML beneficiary"
        open={isOpen}
        onCancel={handleClose}
      >
        <AMLBeneficiaryForm
          item={aml}
          request={request}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}
